// pages/AssetsFormPage/OverlayEditor.jsx
import React, { useMemo, useRef, useState, useEffect } from 'react';
import ChartPreview from './ChartPreview';
import TextPreview from './TextPreview';
import styles from './assetsformpage.module.css';

/**
 * Editor de overlays multi-itens (chart + text + list + table + highlight)
 * - coords normalizadas (x,y,w,h) relativas ao stage (0..1)
 * - page-aware: renderiza somente itens da página atual
 * - drag/resize com handles
 */
export default function OverlayEditor({
  page,
  items,
  onChange,
  profileLabels = [],
  groupLabels = [],
  disabled = false,
  selectedId = null,
  onSelectItem = () => {}
}) {
  const stageRef = useRef(null);
  const [internalSelected, setInternalSelected] = useState(selectedId || null);

  // manter seleção interna em sincronia com prop
  useEffect(() => {
    setInternalSelected(selectedId || null);
  }, [selectedId]);

  const visibleItems = useMemo(
    () => (items || []).filter(it => it.page === page),
    [items, page]
  );

  // limpar seleção se item sumir da página
  useEffect(() => {
    if (!visibleItems.some(it => it.id === internalSelected)) {
      setInternalSelected(null);
    }
  }, [visibleItems, internalSelected]);

  const getStageRect = () => {
    const el = stageRef.current;
    if (!el) return { w: 1, h: 1, left: 0, top: 0 };
    const r = el.getBoundingClientRect();
    return { w: r.width, h: r.height, left: r.left, top: r.top };
  };

  const updateItem = (id, patch) => {
    const next = (items || []).map(it => (it.id === id ? { ...it, ...patch } : it));
    onChange(next);
  };

  const handleSelect = (id) => {
    setInternalSelected(id);
    onSelectItem(id);
  };

  // --- helpers: preset tracking (modo acompanhamento) ---
  const isTrackingPreset = (preset) => String(preset || '').endsWith('_track');
  const getBasePreset = (preset) => String(preset || '').replace(/_track$/, '');

  // drag
  const onMouseDownBox = (e, it) => {
    if (disabled) return;
    if (e.button !== 0) return;
    e.stopPropagation();
    handleSelect(it.id);

    const rect = getStageRect();
    const startX = e.clientX, startY = e.clientY;
    const start = { x: it.x, y: it.y, w: it.w, h: it.h };

    const onMove = (ev) => {
      const dx = (ev.clientX - startX) / rect.w;
      const dy = (ev.clientY - startY) / rect.h;
      const nx = Math.max(0, Math.min(1 - start.w, start.x + dx));
      const ny = Math.max(0, Math.min(1 - start.h, start.y + dy));
      updateItem(it.id, { x: nx, y: ny });
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // resize pelo canto inferior-direito
  const onMouseDownResize = (e, it) => {
    if (disabled) return;
    if (e.button !== 0) return;
    e.stopPropagation();
    handleSelect(it.id);

    const rect = getStageRect();
    const startX = e.clientX, startY = e.clientY;
    const start = { w: it.w, h: it.h, x: it.x, y: it.y };

    const onMove = (ev) => {
      const dw = (ev.clientX - startX) / rect.w;
      const dh = (ev.clientY - startY) / rect.h;
      const nw = Math.max(0.05, Math.min(1 - start.x, start.w + dw));
      const nh = Math.max(0.05, Math.min(1 - start.y, start.h + dh));
      updateItem(it.id, { w: nw, h: nh });
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Labels fictícios
  const defaultProfileLabels = useMemo(
    () => (profileLabels && profileLabels.length ? profileLabels : ['Perfil A', 'Perfil B', 'Perfil C']),
    [profileLabels]
  );
  const defaultGroupLabels = useMemo(
    () => (groupLabels && groupLabels.length ? groupLabels : ['Grupo 1', 'Grupo 2', 'Grupo 3']),
    [groupLabels]
  );

  const getOverlayLabel = (it) => {
    if (!it) return 'Elemento';

    if (it.type === 'chart') {
      const rawPreset = it.preset || 'bar';
      const tracking = isTrackingPreset(rawPreset);
      const preset = getBasePreset(rawPreset);

      const dataMode = it.dataMode || 'profiles';
      let tipo = 'Gráfico';
      if (preset === 'bar') tipo = 'Gráfico de Barras';
      else if (preset === 'column') tipo = 'Gráfico de Colunas';
      else if (preset === 'pie') tipo = 'Gráfico de Pizza';
      else if (preset === 'donut') tipo = 'Gráfico Donut';
      else if (preset === 'radar') tipo = 'Gráfico Radar';

      const modeLabel = dataMode === 'groups' ? 'por Agrupamento' : 'por Perfil';
      const trackLabel = tracking ? ' • Acompanhamento' : '';
      return `${tipo} (${modeLabel})${trackLabel}`;
    }

    if (it.type === 'highlight') {
      return 'Maior / menor indicador';
    }

    if (it.type === 'text') {
      if (it.content?.mode === 'token') {
        if (it.content.key === 'participant.name') return 'Texto: Nome do participante';
        if (it.content.key === 'report.date') return 'Texto: Data do relatório';
        return `Texto: Token`;
      }
      return 'Texto';
    }

    if (it.type === 'list' && it.mode === 'predominantProfiles') {
      return 'Lista de perfis predominantes';
    }

    if (it.type === 'table' && it.mode === 'profileGroupsSummary') {
      return 'Tabela de agrupamentos';
    }

    return 'Elemento';
  };

  const renderHighlightWireframe = () => (
    <div className={styles.highlightSkeleton}>
      <div className={styles.highlightRow}>
        <div className={styles.highlightCircle}>
          <svg className={styles.highlightSvg} viewBox="0 0 40 40">
            <circle cx="20" cy="20" r="17" className={styles.highlightCircleBg} />
            <circle cx="20" cy="20" r="17" className={styles.highlightCircleArcTop} />
          </svg>
          <div className={styles.highlightValue}>9,7</div>
        </div>
        <div className={styles.highlightSide}>
          <div className={`${styles.highlightTitle} ${styles.highlightTitleTop}`}>
            ▲ Maior indicador
          </div>
          <div className={styles.highlightNameTop}>Amor</div>
        </div>
      </div>

      <div className={styles.highlightDivider} />

      <div className={styles.highlightRow}>
        <div className={styles.highlightCircle}>
          <svg className={styles.highlightSvg} viewBox="0 0 40 40">
            <circle cx="20" cy="20" r="17" className={styles.highlightCircleBg} />
            <circle cx="20" cy="20" r="17" className={styles.highlightCircleArcBottom} />
          </svg>
          <div className={styles.highlightValue}>5,8</div>
        </div>
        <div className={styles.highlightSide}>
          <div className={`${styles.highlightTitle} ${styles.highlightTitleBottom}`}>
            ▼ Menor indicador
          </div>
          <div className={styles.highlightNameBottom}>Equilíbrio</div>
        </div>
      </div>
    </div>
  );

  const renderInner = (it) => {
    if (it.type === 'chart') {
      const rawPreset = it.preset || 'bar';
      const tracking = isTrackingPreset(rawPreset);
      const basePreset = getBasePreset(rawPreset) || 'bar';

      const labels =
        (it.dataMode || 'profiles') === 'groups'
          ? defaultGroupLabels
          : defaultProfileLabels;

      // Normal: 1 preview
      if (!tracking) {
        return (
          <div className={styles.chartPreview}>
            <ChartPreview preset={basePreset} labels={labels} showAxis />
          </div>
        );
      }

      // Tracking: 2 previews lado a lado
      return (
        <div className={`${styles.chartPreview} ${styles.chartPreviewTrack}`}>
          <div className={styles.chartPreviewHalf} title="Resultado anterior (preview)">
            <ChartPreview preset={basePreset} labels={labels} showAxis />
          </div>

          <div className={styles.chartPreviewDivider} />

          <div className={styles.chartPreviewHalf} title="Resultado atual (preview)">
            <ChartPreview preset={basePreset} labels={labels} showAxis />
          </div>
        </div>
      );
    }

    if (it.type === 'text') {
      if (it.content?.mode === 'token') {
        const label =
          it.content.key === 'participant.name'
            ? 'Nome do participante'
            : it.content.key === 'report.date'
              ? 'Data do relatório'
              : 'Campo de texto';

        return (
          <div className={styles.textSkeletonBox}>
            <span className={styles.textPreviewInner}>{label}</span>
          </div>
        );
      }

      return (
        <div className={styles.textPreview} data-align={it.style?.align || 'left'}>
          <TextPreview item={it} />
        </div>
      );
    }

    if (it.type === 'list' && it.mode === 'predominantProfiles') {
      return (
        <div className={styles.listSkeleton}>
          <div className={styles.listSkeletonRow}>
            <div className={styles.listSkeletonBullet} />
            <div className={styles.listSkeletonBar} />
          </div>
          <div className={styles.listSkeletonRow}>
            <div className={styles.listSkeletonBullet} />
            <div className={styles.listSkeletonBar} />
          </div>
          <div className={styles.listSkeletonRow}>
            <div className={styles.listSkeletonBullet} />
            <div className={styles.listSkeletonBar} />
          </div>
        </div>
      );
    }

    if (it.type === 'table' && it.mode === 'profileGroupsSummary') {
      return (
        <div className={styles.overlayTableMock}>
          <div className={styles.overlayTableHeader}>
            <span>Agrupamento</span>
            <span>Valor</span>
          </div>
          <div className={styles.overlayTableRow} />
          <div className={styles.overlayTableRow} />
        </div>
      );
    }

    if (it.type === 'highlight') {
      return renderHighlightWireframe();
    }

    return null;
  };

  return (
    <div
      className={styles.overlayEditor}
      ref={stageRef}
      onMouseDown={() => handleSelect(null)}
    >
      {visibleItems.map((it) => {
        const style = {
          left: `${(it.x || 0) * 100}%`,
          top: `${(it.y || 0) * 100}%`,
          width: `${(it.w || 0.2) * 100}%`,
          height: `${(it.h || 0.1) * 100}%`,
          cursor: disabled ? 'default' : 'move'
        };

        const isSelected = it.id === internalSelected;

        return (
          <div
            key={it.id}
            className={`${styles.overlayBox} ${isSelected ? styles.selected : ''}`}
            style={style}
            onMouseDown={(e) => onMouseDownBox(e, it)}
          >
            <div className={styles.overlayBoxLabel}>
              {getOverlayLabel(it)}
            </div>

            <div className={styles.overlayBoxInner}>
              {renderInner(it)}
            </div>

            {!disabled && (
              <div
                className={styles.resizeHandle}
                onMouseDown={(e) => onMouseDownResize(e, it)}
                title="Redimensionar"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}